// ABOUTME: User-facing knowledge base library with category navigation and search
// ABOUTME: Main entry point for browsing articles and videos organized by categories

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Breadcrumbs,
  Link,
  Paper,
} from '@mui/material';
import { Search, Home, Article, VideoLibrary } from '@mui/icons-material';
import { getKBCategories, searchKnowledgeBase } from '../../services/api';
import { KBCategoryDto, KBSearchResultsDto } from '../../types/knowledgeBase';
import CategorySampler from './CategorySampler';
import SearchResults from './SearchResults';
import ErrorBoundary from '../ErrorBoundary';

const KnowledgeBaseLibrary: React.FC = () => {
  const [categories, setCategories] = useState<KBCategoryDto[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<KBCategoryDto | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<KBSearchResultsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0); // 0 = All, 1 = Articles, 2 = Videos

  useEffect(() => {
    loadCategories();
  }, []);

  // Debounced search
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchResults(null);
      return;
    }

    const timeoutId = setTimeout(() => {
      performSearch();
    }, 500);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- performSearch intentionally omitted; it is stable and adding it would conflict with the debounce logic
  }, [searchQuery]);

  const loadCategories = async () => {
    try {
      setError(null);
      const data = await getKBCategories();
      setCategories(data);
    } catch (err) {
      console.error('Failed to load categories:', err);
      setError('Failed to load knowledge base');
    } finally {
      setLoading(false);
    }
  };

  const performSearch = async () => {
    try {
      setSearching(true);
      setError(null);
      const results = await searchKnowledgeBase(searchQuery.trim());
      setSearchResults(results);
    } catch (err) {
      console.error('Search failed:', err);
      setError('Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleCategoryClick = (category: KBCategoryDto) => {
    setSelectedCategory(category);
    setSearchQuery('');
    setSearchResults(null);
  };

  const handleBreadcrumbClick = (category: KBCategoryDto | null) => {
    setSelectedCategory(category);
  };

  const getCategoryBreadcrumbs = (): KBCategoryDto[] => {
    if (!selectedCategory) return [];

    const breadcrumbs: KBCategoryDto[] = [];
    const findPath = (cats: KBCategoryDto[], targetId: number): boolean => {
      for (const cat of cats) {
        if (cat.id === targetId) {
          breadcrumbs.push(cat);
          return true;
        }
        if (cat.childCategories.length > 0) {
          if (findPath(cat.childCategories, targetId)) {
            breadcrumbs.unshift(cat);
            return true;
          }
        }
      }
      return false;
    };

    findPath(categories, selectedCategory.id);
    return breadcrumbs;
  };

  const getCategoriesToDisplay = (): KBCategoryDto[] => {
    if (selectedCategory) {
      return selectedCategory.childCategories;
    }
    return categories; // Root categories
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const breadcrumbs = getCategoryBreadcrumbs();
  const categoriesToDisplay = getCategoriesToDisplay();
  const isSearchActive = searchQuery.trim().length >= 2 && searchResults !== null;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" gutterBottom>
          Knowledge Base
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Find articles, videos, and resources to help you on your parenting journey
        </Typography>
      </Box>

      {/* Search Bar */}
      <Box mb={4}>
        <TextField
          fullWidth
          placeholder="Search articles and videos..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {searching ? <CircularProgress size={20} /> : <Search />}
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search Results View */}
      {isSearchActive ? (
        <SearchResults results={searchResults} query={searchQuery} />
      ) : (
        <>
          {/* Breadcrumbs */}
          {breadcrumbs.length > 0 && (
            <Box mb={3}>
              <Breadcrumbs>
                <Link
                  component="button"
                  variant="body1"
                  onClick={() => handleBreadcrumbClick(null)}
                  sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                >
                  <Home sx={{ mr: 0.5 }} fontSize="small" />
                  Home
                </Link>
                {breadcrumbs.map((cat, idx) => (
                  <Link
                    key={cat.id}
                    component="button"
                    variant="body1"
                    onClick={() => handleBreadcrumbClick(cat)}
                    sx={{ cursor: 'pointer' }}
                    color={idx === breadcrumbs.length - 1 ? 'text.primary' : 'inherit'}
                  >
                    {cat.name}
                  </Link>
                ))}
              </Breadcrumbs>
            </Box>
          )}

          {/* Content Type Filter Tabs */}
          <Paper sx={{ mb: 3 }}>
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} centered>
              <Tab label="All Content" />
              <Tab icon={<Article />} label="Articles" iconPosition="start" />
              <Tab icon={<VideoLibrary />} label="Videos" iconPosition="start" />
            </Tabs>
          </Paper>

          {/* Categories Display */}
          {categoriesToDisplay.length === 0 ? (
            <Box textAlign="center" py={8}>
              <Typography variant="h6" color="text.secondary">
                {selectedCategory
                  ? 'No subcategories or content in this category yet.'
                  : 'No content available yet. Check back soon!'}
              </Typography>
            </Box>
          ) : (
            <Box>
              {categoriesToDisplay.map((category) => (
                <ErrorBoundary key={category.id}>
                  <CategorySampler
                    category={category}
                    contentTypeFilter={activeTab === 1 ? 'articles' : activeTab === 2 ? 'videos' : 'all'}
                    onCategoryClick={handleCategoryClick}
                  />
                </ErrorBoundary>
              ))}
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default KnowledgeBaseLibrary;
